import {Fill, Stroke, Style} from "ol/style";
import CircleStyle from "ol/style/Circle";
import {FillPatternTypeEnum, GeometryTypeEnum, LinePatternTypeEnum, StyleRuleTypeEnum} from "../enum/enum";
import {isGraphicLowContrastOnWhite} from "../colors";
import parseCSSColor from "parse-css-color";
import * as $ from "jquery";
import {drawDiagonalLines, drawDotted, drawHorizontalLines, drawVerticalLines} from "../draw";
import {Base} from "../Base";
import {isNumber, toNumber} from "../util";

/**
 * A styler. Given a style configuration, produces OpenLayers styles
 * @extends Base
 */
class Styler extends Base {
    /**
     *
     * @param styleConfig {Style} the style configuration
     */
    constructor(styleConfig) {
        super();
        this.styleConfig = styleConfig;
    }


    getClassName() {
        return "Styler";
    }

    getOLStyle() {
        return this._getOLStyle(false);
    }

    getOLHighlightStyle() {
        return this._getOLStyle(true);
    }

    getClusterImageStyle() {
        const clusterStyler = this.styleConfig.getRules()
            .filter(r => r.getStyleRuleType() === StyleRuleTypeEnum.CLUSTER)
            .map(r => new SimpleStylerRule(r, this.styleConfig.getGeometryType()))[0];
        return clusterStyler
            ? clusterStyler.getOLStyle(false)()[0].getImage()
            : new CircleStyle({
                radius: 10,
                stroke: new Stroke({color: 'white'}),
                fill: new Fill({color: '#3399CC'})
            });
    }

    _getOLStyle(highlight) {
        const configRules = this.styleConfig.getRules();
        let noDataRuleConfig = configRules.filter(function (r) {
            return r.getStyleRuleType() === StyleRuleTypeEnum.NODATA
        });
        noDataRuleConfig = noDataRuleConfig.length > 0 ? noDataRuleConfig[0] : undefined;

        let stylerRules = [];
        for (let r in configRules) {
            let configRule = configRules[r];
            switch (configRule.getStyleRuleType()) {
                case StyleRuleTypeEnum.CLASSIFIED:
                    stylerRules.push(new ClassifiedStylerRule(configRule, this.styleConfig.getGeometryType(), this.styleConfig.getAttribute()));
                    break;
                case StyleRuleTypeEnum.CONTINUOUS:
                    stylerRules.push(new ContinuousStylerRule(configRule, this.styleConfig.getGeometryType(), this.styleConfig.getAttribute()));
                    break;
                case StyleRuleTypeEnum.DISCRETE:
                    stylerRules.push(new DiscreteStylerRule(configRule, this.styleConfig.getGeometryType(), this.styleConfig.getAttribute()));
                    break;
                case StyleRuleTypeEnum.SIMPLE:
                    stylerRules.push(new SimpleStylerRule(configRule, this.styleConfig.getGeometryType()));
                    break;
                default:
                    break;
            }
        }
        if (noDataRuleConfig) {
            stylerRules.push(new SimpleStylerRule(noDataRuleConfig, this.styleConfig.getGeometryType()));
        }

        let previous = undefined;
        for (let s in stylerRules) {
            if (previous)
                previous.setNextStylerRule(stylerRules[s]);
            previous = stylerRules[s];
        }

        return stylerRules.length > 0 ? stylerRules[0].getOLStyle(highlight) : undefined;
    }
}

/**
 * A styler for charts
 * @extends Base
 */
class ChartStyler extends Base {
    /**
     *
     * @param styleConfig {Style} the style configuration
     */
    constructor(styleConfig) {
        super();
        this.styleConfig = styleConfig;
    }

    getClassName() {
        return "ChartStyler";
    }

    getAttributeName() {
        return this.styleConfig.getAttribute()
    }

    getFirstRuleType() {
        const rules = this.styleConfig.getRules();
        return rules && rules.length > 0 ? rules[0].getStyleRuleType() : undefined;
    }

    getPlotlyColorscale() {
        const colorScale = [];
        if (this.styleConfig) {
            this.styleConfig.getRules().forEach(rule => {
                const styleRuleType = rule.getStyleRuleType();
                if (styleRuleType === StyleRuleTypeEnum.CONTINUOUS) {
                    rule.getRanges().forEach(range => {
                        colorScale.push([range.getMinValue(), range.getMinBackgroundColor()]);
                        colorScale.push([range.getMaxValue(), range.getMaxBackgroundColor()]);
                    });
                } else if (styleRuleType === StyleRuleTypeEnum.DISCRETE) {
                    rule.getBuckets().forEach(bucket => {
                        colorScale.push([bucket.getMinValue(), bucket.getBackgroundColor()]);
                        colorScale.push([bucket.getMaxValue(), bucket.getBackgroundColor()]);
                    });
                } else if (styleRuleType === StyleRuleTypeEnum.CLASSIFIED) {
                    rule.getClasses().forEach(clazz => {
                        colorScale.push([clazz.getValue(), clazz.getBackgroundColor()])
                    });
                }
            });
        }
        return colorScale;
    }

    getMinMaxColor() {
        let colors = [], minValue, maxValue;
        if (this.styleConfig) {
            this.styleConfig.getRules().forEach(rule => {
                const styleRuleType = rule.getStyleRuleType();
                if (styleRuleType === StyleRuleTypeEnum.CONTINUOUS) {
                    rule.getRanges().forEach(range => {
                        if (range.getMinValue() === undefined || minValue === undefined || range.getMinValue() < minValue) {
                            minValue = range.getMinValue();
                            colors[0] = range.getMinBackgroundColor();
                        }
                        if (range.getMaxValue() === undefined || maxValue === undefined || range.getMaxValue() > maxValue) {
                            maxValue = range.getMaxValue();
                            colors[1] = range.getMaxBackgroundColor();
                        }
                    });
                } else if (styleRuleType === StyleRuleTypeEnum.DISCRETE) {
                    rule.getBuckets().forEach(bucket => {
                        if (bucket.getMinValue() === undefined || minValue === undefined || bucket.getMinValue() < minValue) {
                            minValue = bucket.getMinValue();
                            colors[0] = bucket.getBackgroundColor();
                        }
                        if (bucket.getMaxValue() === undefined || maxValue === undefined || bucket.getMaxValue() > maxValue) {
                            maxValue = bucket.getMaxValue();
                            colors[1] = bucket.getBackgroundColor();
                        }
                    });
                }
            });
        }
        return colors;
    }

    getIsNoDataFunction() {
        const stylerRules = this._getStyleRules();
        return (value) => {
            if (value === undefined || value === null)
                return true;
            return stylerRules.filter(sr => sr.getIsInScopeFunction()(value)).length === 0;
        }
    }

    getColorFunction() {
        const stylerRules = this._getStyleRules();
        return (value) => {
            for (let f in stylerRules) {
                const color = stylerRules[f].getColorFunction()(value);
                if (color)
                    return color;
            }
            return undefined;
        }
    }

    getLabelFunction(noMinMax = false) {
        const stylerRules = this._getStyleRules();
        return (value) => {
            for (let f in stylerRules) {
                const label = stylerRules[f].getLabelFunction(noMinMax)(value);
                if (label)
                    return label;
            }
            return undefined;
        }
    }

    getYAxisLabel() {
        const rules = this.styleConfig.getRules();
        return rules.length > 0 ? rules[0].getLabel() : undefined;
    }

    _getStyleRules() {
        const configRules = this.styleConfig.getRules();
        const noDataRuleConfig = configRules.filter(function (r) {
            return r.getStyleRuleType() === StyleRuleTypeEnum.NODATA
        })[0];

        const stylerRules = [];
        this.styleConfig.getRules().forEach(configRule => {
            switch (configRule.getStyleRuleType()) {
                case StyleRuleTypeEnum.CLASSIFIED:
                    stylerRules.push(new ClassifiedStylerRule(configRule, this.styleConfig.getGeometryType(), this.styleConfig.getAttribute()));
                    break;
                case StyleRuleTypeEnum.CONTINUOUS:
                    stylerRules.push(new ContinuousStylerRule(configRule, this.styleConfig.getGeometryType(), this.styleConfig.getAttribute()));
                    break;
                case StyleRuleTypeEnum.DISCRETE:
                    stylerRules.push(new DiscreteStylerRule(configRule, this.styleConfig.getGeometryType(), this.styleConfig.getAttribute()));
                    break;
                case StyleRuleTypeEnum.SIMPLE:
                    stylerRules.push(new SimpleStylerRule(configRule, this.styleConfig.getGeometryType()));
                    break;
                default:
                    break;
            }
        });

        if (noDataRuleConfig) {
            stylerRules.push(new SimpleStylerRule(noDataRuleConfig, this.styleConfig.getGeometryType()));
        }

        let previous = undefined;
        stylerRules.forEach(stylerRule => {
            if (previous)
                previous.setNextStylerRule(stylerRule);
            previous = stylerRule;
        })
        return stylerRules;
    }

    isClassified() {
        return this.styleConfig.getRules().filter(r => r.getStyleRuleType() === StyleRuleTypeEnum.CLASSIFIED).length > 0;
    }

    isDiscrete() {
        return this.styleConfig.getRules().filter(r => r.getStyleRuleType() === StyleRuleTypeEnum.DISCRETE).length > 0;
    }

    isOnlyDiscrete() {
        const ruleCount = this.styleConfig.getRules().length;
        return ruleCount > 0 && this.styleConfig.getRules().filter(r => r.getStyleRuleType() === StyleRuleTypeEnum.DISCRETE).length === ruleCount;
    }

    getValueLegendIndex(value) {
        if (value === 0 || value === undefined)
            return undefined;

        if (this._getStyleRules()[0].getType() === StyleRuleTypeEnum.SIMPLE)
            return 0;

        if (this.isClassified()) {
            const classifiedStylerRule = this._getStyleRules()[0];
            const classes = classifiedStylerRule._getClasses();
            for (let i = 0; i < classes.length; i++)
                if (classes[i].getValue() === value)
                    return i;
            return undefined;
        } else {
            let i = 0;
            for (let styleRule of this._getStyleRules()) {
                if (styleRule.getType() === StyleRuleTypeEnum.DISCRETE) {
                    for (let bucket of styleRule._getBuckets()) {
                        i++;
                        if ((bucket.getMinValue() === undefined || bucket.getMinValue() <= value)
                            && (bucket.getMaxValue() === undefined || bucket.getMaxValue() >= value)) {
                            return i;
                        }
                    }
                } else if (styleRule.getType() === StyleRuleTypeEnum.CONTINUOUS) {
                    for (let range of styleRule._getRanges()) {
                        i++;
                        if ((range.getMinValue() === undefined || range.getMinValue() <= value)
                            && (range.getMaxValue() === undefined || range.getMaxValue() >= value)) {
                            return i;
                        }
                    }
                }
            }
        }
    }
}

/**
 * Any styler rule
 * @extends Base
 */
class StylerRule extends Base {
    /**
     *
     * @param styleRuleConfig {StyleRule} the style rule configuration
     * @param geometryType {GeometryTypeEnum} the type of the geometry
     */
    constructor(styleRuleConfig, geometryType) {
        super();
        this.styleRuleConfig = styleRuleConfig;
        this.geometryType = geometryType;
    }

    getClassName() {
        return "StylerRule";
    }

    getType() {
        return this.styleRuleConfig.getStyleRuleType();
    }

    /**
     * @param nextStylerRule {StylerRule} The next stylerRule to apply, if this one does not fit. Can be undefined
     */
    setNextStylerRule(nextStylerRule) {
        this.nextStylerRule = nextStylerRule;
    }

    /**
     * Get an OpenLayers style suitable to render a vector layer
     * @param highlight {boolean} is this style meant to highlight features?
     * @return {function} function(Feature,Number)->Style
     */
    getOLStyle(highlight) {
    }

    /**
     * A function that given value {string|number}, get a color, applying this style to the value
     * @return {function} that returns a CSS color
     */
    getColorFunction() {
    }

    /**
     * A function that given value {string|number}, returns true if it is un scope of this rule
     * @return {function} true if value in scope of this rule
     */
    getIsInScopeFunction() {
        return value => true;
    }

    readValue(feature) {
        if (this.attribute) {
            if (this.attribute.indexOf('.') > 0) {
                const path = this.attribute.split('.');
                let val = feature.getProperties();
                if (val.features && val.features.length > 1) { //deal with clustered layers
                    return undefined;
                }
                if (val.features && val.features.length === 1) { //deal with clustered layers
                    val = val.features[0].getProperties();
                }
                path.forEach(p => {
                    val = val[p];
                });
                return val;
            } else return feature.get(this.attribute);
        }
    }


    /**
     * A function that given value {string|number}, get a label, applying this style to the value
     * @return {function} that returns string
     */
    getLabelFunction() {
    }

    getCircleOLOptions(radius, fillColor, borderColor, borderWidth, highlight) {
        const res = {
            radius: radius,
            fill: new Fill({color: fillColor})
        };
        if (borderColor) {
            if (highlight) {
                if (isGraphicLowContrastOnWhite(borderColor)) {
                    res.stroke = new Stroke({color: 'black', width: 3});
                } else {
                    res.stroke = new Stroke({color: 'white', width: 3});
                }
            } else {
                res.stroke = new Stroke({color: borderColor, width: borderWidth});
            }
        } else if (highlight) {
            res.stroke = new Stroke({color: 'white', width: 3})
        }
        return res;
    }

    getLineOLStrokes(linePattern, borderColor, borderWidth, highlight) {
        const res = {
            width: borderWidth
        };
        const strokes = [];
        if (linePattern) {
            const transparentOptions = {};
            switch (linePattern) {
                case LinePatternTypeEnum.DASHED:
                    res.lineDash = [10, 8];
                    transparentOptions.lineDash = [8, 10];
                    transparentOptions.lineDashOffset = 8;
                    break;
                case LinePatternTypeEnum.DOTTED:
                    res.lineDash = [0.5, 8];
                    transparentOptions.lineDash = [8, 0.5];
                    transparentOptions.lineDashOffset = 8;
                    break;
                default:
                    break;
            }
            transparentOptions.color = 'rgba(0,0,0,0)';
            transparentOptions.width = highlight ? 2 : borderWidth;
            strokes.push(new Stroke(transparentOptions));
        }
        if (highlight) {
            if (isGraphicLowContrastOnWhite(borderColor)) {
                res.color = 'black';
            } else {
                res.color = 'white';
            }
            res.width = 2;
        } else {
            res.color = borderColor;
        }

        strokes.push(new Stroke(res));
        return strokes;
    }

    getPolygonOLStyles(borderColor, borderWidth, backgroundColor, fillPattern, highlight) {
        return this.getPolygonWithBorderPatternOLStyles(borderColor, borderWidth, undefined, backgroundColor, fillPattern, highlight);
    }

    getPolygonWithBorderPatternOLStyles(borderColor, borderWidth = .5, borderPattern, backgroundColor, fillPattern, highlight) {
        const fill = new Fill();
        if (fillPattern) {
            const patternCanvas = this._drawPolygonPattern(fillPattern.getColor(), fillPattern.getWidth(), fillPattern.getFillPatternType());
            const canvas = document.createElement('canvas');
            const ctx = canvas.getContext('2d');
            const pattern = ctx.createPattern(patternCanvas, 'repeat');
            fill.setColor(pattern);
        } else {
            fill.setColor(backgroundColor);
        }

        const style = new Style({
            fill: fill
        });

        let stroke;
        if (borderColor) {
            stroke = new Stroke({
                width: borderWidth
            });

            if (highlight) {
                stroke.setColor(isGraphicLowContrastOnWhite(borderColor) ? 'black' : 'white');
            } else {
                stroke.setColor(borderColor);
            }
            style.setStroke(stroke)
        } else if (highlight) {
            stroke = new Stroke({
                width: 2,
                color: isGraphicLowContrastOnWhite(backgroundColor) ? 'black' : 'white'
            });
            style.setStroke(stroke)
        }
        if (borderPattern && borderPattern === LinePatternTypeEnum.DOTTED) {
            stroke.setLineDash([0.5, 8]);
        }
        if (borderPattern && borderPattern === LinePatternTypeEnum.DASHED) {
            stroke.setLineDash([10, 8]);
        }

        return [style];
    }

    _drawPolygonPattern(color, thickness, fillType) {
        const GRAPHIC_WIDTH = 50;
        const GRAPHIC_HEIGHT = 50;
        const canvas = $('<canvas width="' + GRAPHIC_WIDTH + '" height="' + GRAPHIC_HEIGHT + '"></canvas>')[0];
        const context = canvas.getContext("2d");
        switch (fillType) {
            case FillPatternTypeEnum.DIAGONAL_CROSS:
                drawDiagonalLines(context, color, thickness, GRAPHIC_WIDTH, GRAPHIC_HEIGHT, GRAPHIC_WIDTH / (thickness * (GRAPHIC_WIDTH / 12)), true);
                drawDiagonalLines(context, color, thickness, GRAPHIC_WIDTH, GRAPHIC_HEIGHT, GRAPHIC_WIDTH / (thickness * (GRAPHIC_WIDTH / 12)), false);
                break;
            case FillPatternTypeEnum.DIAGONAL:
                drawDiagonalLines(context, color, thickness, GRAPHIC_WIDTH, GRAPHIC_HEIGHT, GRAPHIC_WIDTH / (thickness * (GRAPHIC_WIDTH / 12)), false);
                break;
            case FillPatternTypeEnum.ANTI_DIAGONAL:
                drawDiagonalLines(context, color, thickness, GRAPHIC_WIDTH, GRAPHIC_HEIGHT, GRAPHIC_WIDTH / (thickness * (GRAPHIC_WIDTH / 12)), true);
                break;
            case FillPatternTypeEnum.HORIZONTAL:
                drawHorizontalLines(context, color, thickness, GRAPHIC_WIDTH, GRAPHIC_HEIGHT, 5);
                break;
            case FillPatternTypeEnum.VERTICAL:
                drawVerticalLines(context, color, thickness, GRAPHIC_WIDTH, GRAPHIC_HEIGHT, 5);
                break;
            case FillPatternTypeEnum.CROSS:
                drawHorizontalLines(context, color, thickness, GRAPHIC_WIDTH, GRAPHIC_HEIGHT, 5);
                drawVerticalLines(context, color, thickness, GRAPHIC_WIDTH, GRAPHIC_HEIGHT, 5);
                break;
            case FillPatternTypeEnum.DOTTED:
                drawDotted(context, color, GRAPHIC_WIDTH + 2, GRAPHIC_HEIGHT + 2, 12, 12, 2);
                break;
            default:
                break;
        }
        return canvas;
    }
}

/**
 * A Classified Style
 * @extends StylerRule
 */
class ClassifiedStylerRule extends StylerRule {

    /**
     *
     * @param styleRule {StyleRule} the style rule configuration
     * @param geometryType {GeometryTypeEnum} the type of the geometry
     * @param attribute {string} the name of the attribute where the classification is
     */
    constructor(styleRule, geometryType, attribute) {
        super(styleRule, geometryType);
        this.attribute = attribute;
    }

    getClassName() {
        return "ClassifiedStylerRule";
    }

    _getClasses() {
        return this.styleRuleConfig.getClasses();
    }

    getOLStyle(highlight) {
        const classes = this._getClasses();
        return (feature, resolution) => {
            const originalValue = this.readValue(feature);
            const value = originalValue !== undefined ? '' + originalValue : undefined;
            let foundClass = false;
            for (let c in classes) {
                const clazz = classes[c];
                const classValue = clazz.getValue();
                if (classValue === value || (!foundClass && classValue === undefined)) {
                    foundClass = true;
                    if (this.geometryType === GeometryTypeEnum.POINT) {
                        return [new Style({
                            image: new CircleStyle(this.getCircleOLOptions(
                                clazz.getRadius(),
                                clazz.getBackgroundColor(),
                                clazz.getBorderColor(),
                                clazz.getBorderWidth(),
                                highlight)
                            )
                        })];
                    }
                    if (this.geometryType === GeometryTypeEnum.LINE) {
                        const linePattern = clazz.getLinePattern();
                        if (linePattern) {
                            const lineOLStrokes = this.getLineOLStrokes(
                                linePattern.getLinePatternType(),
                                linePattern.getColor(),
                                linePattern.getWidth(),
                                highlight);
                            return lineOLStrokes.map(s => new Style({stroke: s}));
                        }
                        const lineOLStrokes = this.getLineOLStrokes(
                            undefined,
                            clazz.getBorderColor(),
                            clazz.getBorderWidth(),
                            highlight);
                        return lineOLStrokes.map(s => new Style({stroke: s}));
                    }
                    if (this.geometryType === GeometryTypeEnum.POLYGON) {
                        const linePattern = clazz.getLinePattern();
                        if (linePattern) {
                            return this.getPolygonWithBorderPatternOLStyles(
                                linePattern.getColor(),
                                linePattern.getWidth(),
                                linePattern.getLinePatternType(),
                                clazz.getBackgroundColor(),
                                clazz.getFillPattern(),
                                highlight);
                        }
                        return this.getPolygonOLStyles(
                            clazz.getBorderColor(),
                            clazz.getBorderWidth(),
                            clazz.getBackgroundColor(),
                            clazz.getFillPattern(),
                            highlight);
                    }
                }
            }
            if (this.nextStylerRule) {
                return this.nextStylerRule.getOLStyle(highlight)(feature, resolution);
            }
        };
    }

    getColorFunction() {
        const classes = this._getClasses();
        return value => {
            value = value !== undefined && value !== null ? value.toString() : value;
            const color = classes
                .filter(c => c.getValue() === value)
                .map(c => c.getBackgroundColor())[0];
            if (color)
                return color;
            if (value)
                return "rgba(0,0,0,0)";
            if (this.nextStylerRule) {
                return this.nextStylerRule.getColorFunction()(value);
            }
        };
    }

    getIsInScopeFunction() {
        const classes = this._getClasses();
        return value => {
            if (value === undefined || value === null)
                return false;
            value = value.toString();
            return classes
                .filter(c => c.getValue() === value).length > 0;
        };
    }

    getLabelFunction() {
        const classes = this._getClasses();
        return value => {
            value = value !== undefined && value !== null ? value.toString() : value;
            const label = classes
                .filter(c => c.getValue() === value)
                .map(c => c.getLabel())[0];
            if (label)
                return label;
            if (value)
                return "NO_DATA";
            if (this.nextStylerRule) {
                return this.nextStylerRule.getLabelFunction()(value);
            }
        };
    }
}

/**
 * A Discrete Style
 * @extends StylerRule
 */
class DiscreteStylerRule extends StylerRule {

    /**
     *
     * @param styleRule {StyleRule} the style rule configuration
     * @param geometryType {GeometryTypeEnum} the type of the geometry
     * @param attribute {string} the name of the attribute where the value is
     */
    constructor(styleRule, geometryType, attribute) {
        super(styleRule, geometryType);
        this.attribute = attribute;
    }

    getClassName() {
        return "DiscreteStylerRule";
    }

    getOLStyle(highlight) {
        const buckets = this._getBuckets();
        const styleFunction = (feature, resolution) => {
            const value = this.readValue(feature);
            if (isNumber((value))) {
                for (let b in buckets) {
                    const bucket = buckets[b];
                    if ((bucket.getMinValue() === undefined || bucket.getMinValue() <= value)
                        && (bucket.getMaxValue() === undefined || bucket.getMaxValue() >= value)) {
                        if (this.geometryType === GeometryTypeEnum.POINT) {
                            return [new Style({
                                image: new CircleStyle(this.getCircleOLOptions(
                                    bucket.getRadius(),
                                    bucket.getBackgroundColor(),
                                    bucket.getBorderColor(),
                                    bucket.getBorderWidth(),
                                    highlight)
                                )
                            })];
                        }
                        if (this.geometryType === GeometryTypeEnum.LINE) {
                            const lineOLStrokes = this.getLineOLStrokes(
                                undefined,
                                bucket.getBorderColor(),
                                bucket.getBorderWidth(),
                                highlight);
                            return lineOLStrokes.map(s => new Style({stroke: s}));
                        }
                        if (this.geometryType === GeometryTypeEnum.POLYGON) {
                            return this.getPolygonOLStyles(
                                bucket.getBorderColor(),
                                bucket.getBorderWidth(),
                                bucket.getBackgroundColor(),
                                undefined,
                                highlight);
                        }
                    }
                }
            }
            if (this.nextStylerRule) {
                return this.nextStylerRule.getOLStyle(highlight)(feature, resolution);
            }
        }
        return styleFunction;
    }

    readValue(feature) {
        return toNumber(super.readValue(feature));
    }

    getColorFunction() {
        const buckets = this._getBuckets();
        return value => {
            if (isNumber(value)) {
                const color = buckets
                    .filter(bucket => (bucket.getMinValue() === undefined || bucket.getMinValue() <= value)
                        && (bucket.getMaxValue() === undefined || bucket.getMaxValue() >= value))
                    .map(b => b.getBackgroundColor())[0];
                if (color)
                    return color;
            }
            if (this.nextStylerRule) {
                return this.nextStylerRule.getColorFunction()(value);
            }
        };
    }

    getIsInScopeFunction() {
        const buckets = this._getBuckets();
        return value => {
            if (value === undefined || value === null)
                return false;
            if (isNumber(value)) {
                return buckets
                    .filter(bucket => (bucket.getMinValue() === undefined || bucket.getMinValue() <= value)
                        && (bucket.getMaxValue() === undefined || bucket.getMaxValue() >= value))
                    .length > 0
            }
        };
    }

    _getBuckets() {
        return this.styleRuleConfig.getBuckets();
    }

    getLabelFunction(noMinMax = false) {
        const buckets = this._getBuckets();
        return value => {
            if (isNumber(value)) {
                return buckets
                    .filter(bucket => (bucket.getMinValue() === undefined || bucket.getMinValue() <= value)
                        && (bucket.getMaxValue() === undefined || bucket.getMaxValue() >= value))
                    .map(b => {
                        if (b.getLabel())
                            return b.getLabel();
                        if (!noMinMax && b.getMinLabel() && b.getMaxLabel())
                            return b.getMinLabel() + " - " + b.getMaxLabel();
                        if (noMinMax)
                            return null;
                        return b.getMinLabel()
                            ? b.getMinLabel()
                            : (b.getMaxLabel() ? b.getMaxLabel() : null);
                    })[0];
            }
            if (this.nextStylerRule) {
                return this.nextStylerRule.getLabelFunction(noMinMax)(value);
            }
        };
    }
}

/**
 * A Continuous Style
 * @extends StylerRule
 */
class ContinuousStylerRule extends StylerRule {

    /**
     *
     * @param styleRule {StyleRule} the style rule configuration
     * @param geometryType {GeometryTypeEnum} the type of the geometry
     * @param attribute {string} the name of the attribute where the value is
     */
    constructor(styleRule, geometryType = GeometryTypeEnum.POLYGON, attribute = undefined) {
        super(styleRule, geometryType);
        this.attribute = attribute;
    }

    getClassName() {
        return "ContinuousStylerRule";
    }

    _interpolate(value, minColor, maxColor, minValue, maxValue) {
        const rMin = minColor.values[0];
        const gMin = minColor.values[1];
        const bMin = minColor.values[2];
        const aMin = minColor.alpha;
        const rMax = maxColor.values[0];
        const gMax = maxColor.values[1];
        const bMax = maxColor.values[2];
        const aMax = maxColor.alpha;

        const steps = 10000.0;
        const zeroedMaxValue = maxValue - minValue;
        const zeroedValue = value - minValue;
        const valueStep = zeroedMaxValue > 0 ? (zeroedValue * steps / zeroedMaxValue) : 0;
        const interpolatedR = (rMax - rMin) * valueStep / steps + rMin;
        const interpolatedG = (gMax - gMin) * valueStep / steps + gMin;
        const interpolatedB = (bMax - bMin) * valueStep / steps + bMin;
        const interpolatedA = (aMax - aMin) * valueStep / steps + aMin;
        return 'rgba(' + [interpolatedR.toFixed(0), interpolatedG.toFixed(0), interpolatedB.toFixed(0), interpolatedA].join(',') + ')';
    }

    readValue(feature) {
        return toNumber(super.readValue(feature));
    }

    getColorFunction() {
        const ranges = this._getRanges();
        return value => {
            if (isNumber(value)) {
                const color = ranges
                    .filter(range => (range.getMinValue() === undefined || range.getMinValue() <= value)
                        && (range.getMaxValue() === undefined || range.getMaxValue() >= value))
                    .map(r => {
                        const minColor = parseCSSColor(r.getMinBackgroundColor());
                        const maxColor = parseCSSColor(r.getMaxBackgroundColor());
                        return this._interpolate(value, minColor, maxColor, r.getMinValue(), r.getMaxValue());
                    })[0];
                if (color)
                    return color;
            }
            if (this.nextStylerRule) {
                return this.nextStylerRule.getColorFunction()(value);
            }
        };
    }

    getIsInScopeFunction() {
        const ranges = this._getRanges();
        return value => {
            if (value === undefined || value === null)
                return false;
            if (isNumber(value)) {
                return ranges
                    .filter(range => (range.getMinValue() === undefined || range.getMinValue() <= value)
                        && (range.getMaxValue() === undefined || range.getMaxValue() >= value))
                    .length > 0
            }
        };
    }

    _getRanges() {
        return this.styleRuleConfig.getRanges();
    }

    getLabelFunction() {
        const ranges = this._getRanges();
        return value => {
            if (isNumber(value)) {
                return ranges
                    .filter(range => (range.getMinValue() === undefined || range.getMinValue() <= value)
                        && (range.getMaxValue() === undefined || range.getMaxValue() >= value))
                    .map(r => {
                        return r.getMinValue() === value
                            ? r.getMinLabel()
                            : (r.getMaxValue() === value
                                ? r.getMaxLabel()
                                : null);
                    })[0];
            }
            if (this.nextStylerRule) {
                return this.nextStylerRule.getLabelFunction()(value);
            }
        };
    }

    getOLStyle(highlight) {
        const ranges = this._getRanges();
        return (feature, resolution) => {
            const value = this.readValue(feature);
            if (isNumber(value)) {
                for (let r in ranges) {
                    const range = ranges[r];
                    if ((range.getMinValue() === undefined || range.getMinValue() <= value)
                        && (range.getMaxValue() === undefined || range.getMaxValue() >= value)) {
                        const minColor = parseCSSColor(range.getMinBackgroundColor());
                        const maxColor = parseCSSColor(range.getMaxBackgroundColor());
                        const color = this._interpolate(value, minColor, maxColor, range.getMinValue(), range.getMaxValue());
                        if (this.geometryType === GeometryTypeEnum.POINT) {
                            return [new Style({
                                image: new CircleStyle(this.getCircleOLOptions(
                                    range.getRadius(),
                                    color,
                                    range.getBorderColor(),
                                    range.getBorderWidth(),
                                    highlight)
                                )
                            })];
                        }
                        if (this.geometryType === GeometryTypeEnum.LINE) {
                            const lineOLStrokes = this.getLineOLStrokes(
                                undefined,
                                color,
                                range.getBorderWidth(),
                                highlight);
                            return lineOLStrokes.map(s => new Style({stroke: s}));
                        }

                        if (this.geometryType === GeometryTypeEnum.POLYGON) {
                            return this.getPolygonOLStyles(
                                range.getBorderColor(),
                                range.getBorderWidth(),
                                color,
                                undefined,
                                highlight);
                        }
                    }
                }
            }
            if (this.nextStylerRule) {
                return this.nextStylerRule.getOLStyle(highlight)(feature, resolution);
            }
        };
    }
}

/**
 * A simple style
 * @extends StylerRule
 * @hideconstructor
 */
class SimpleStylerRule extends StylerRule {


    getClassName() {
        return "SimpleStylerRule";
    }

    getOLStyle(highlight) {
        return (feature, resolution) => {
            if (this.geometryType === GeometryTypeEnum.POINT) {
                return [new Style({
                    image: new CircleStyle(this.getCircleOLOptions(
                        this.styleRuleConfig.getRadius(),
                        this.styleRuleConfig.getBackgroundColor(),
                        this.styleRuleConfig.getBorderColor(),
                        this.styleRuleConfig.getBorderWidth(),
                        highlight)
                    )
                })];
            } else if (this.geometryType === GeometryTypeEnum.LINE) {
                const lineOLStrokes = this.getLineOLStrokes(
                    this.styleRuleConfig.getLinePattern().getLinePatternTypeEnum(),
                    this.styleRuleConfig.getBorderColor(),
                    this.styleRuleConfig.getBorderWidth(),
                    highlight);
                return lineOLStrokes.map(s => new Style({stroke: s}));
            } else if (this.geometryType === GeometryTypeEnum.POLYGON) {
                const linePattern = this.styleRuleConfig.getLinePattern();
                if (linePattern) {
                    return this.getPolygonWithBorderPatternOLStyles(
                        linePattern.getColor(),
                        linePattern.getWidth(),
                        linePattern.getLinePatternType(),
                        this.styleRuleConfig.getBackgroundColor(),
                        this.styleRuleConfig.getFillPattern(),
                        highlight);
                }
                return this.getPolygonOLStyles(
                    this.styleRuleConfig.getBorderColor(),
                    this.styleRuleConfig.getBorderWidth(),
                    this.styleRuleConfig.getBackgroundColor(),
                    this.styleRuleConfig.getFillPattern(),
                    highlight);
            }
        }
    }

    isNoData() {
        return this.styleRuleConfig.getStyleRuleType() === StyleRuleTypeEnum.NODATA;
    }

    getColorFunction() {
        return value => {
            if (this.isNoData() && (value === null || value === undefined))
                return this.styleRuleConfig.getBackgroundColor();
            if (!this.isNoData())
                return this.styleRuleConfig.getBackgroundColor();
        };
    }

    getLabelFunction() {
        return value => {
            if (this.isNoData() && (value === null || value === undefined))
                return this.styleRuleConfig.getLabel();
            if (!this.isNoData())
                return this.styleRuleConfig.getLabel();
        };
    }
}

export {Styler, ChartStyler}